import { type ReactElement, useState } from "react";

import { FormField } from "@/components/FormField";
import { HelpTip } from "@/components/HelpTip";
import { PageHeading } from "@/components/PageHeading";
import { SaveForm } from "@/components/SaveForm";
import { Section } from "@/components/Section";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type ActionOutcome,
  type Grants,
  LOAD_FAILED,
  ROLE_MISSING,
  loadGrants,
  removeGrantRole,
  saveGrantRole,
  saveGrants,
  useResource,
} from "@/lib/api";
import { ELEMENT_TIPS } from "@/lib/help";
import type { BackofficeElement } from "@/lib/labels";

type Matrix = Readonly<Record<string, ReadonlySet<string>>>;

const NO_ROLE_HELP =
  "Ohne Häkchen bedient das Element nur der Superadmin. Ein Häkchen gilt ab der nächsten Anfrage, auch für angemeldete Personen.";
const ROLE_HELP =
  "Der Name muss genau so lauten wie die Rolle am Backoffice-Client im Anmeldedienst. Wer die Rolle hat, legt die IT dort fest; hier steht nur, was sie öffnet.";

function matrixOf(data: Grants): Matrix {
  return Object.fromEntries(
    data.pages.flatMap((page) => page.rows.map((row) => [row.element, new Set(row.roles)])),
  );
}

function sameMatrix(a: Matrix, b: Matrix): boolean {
  const elements = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...elements].every((element) => {
    const left = a[element] ?? new Set<string>();
    const right = b[element] ?? new Set<string>();
    return left.size === right.size && [...left].every((role) => right.has(role));
  });
}

/**
 * The grants page (D57): which client role opens which form, section or button. The
 * superadmin's alone, like its navigation entry; the server refuses everybody else.
 *
 * One table per page of the backoffice, one row per element, one column per role: the roles the
 * superadmin entered first (»Vertrieb«, »Redaktion«), the nine built-in ones after them. The
 * whole matrix is saved at once. »Startwert« puts back what a row started with under D42.
 */
export function PermissionsPage({ superadmin }: { readonly superadmin: boolean }): ReactElement {
  if (!superadmin) {
    return (
      <>
        <PageHeading title="Berechtigungen" />
        <p className="text-sm text-muted-foreground">{ROLE_MISSING}</p>
      </>
    );
  }
  return <LoadedPermissions />;
}

function LoadedPermissions(): ReactElement {
  const grants = useResource<Grants>(loadGrants);
  if (grants.status !== "ready") {
    return (
      <>
        <PageHeading title="Berechtigungen" />
        <p className="text-sm text-muted-foreground">
          {grants.status === "loading" ? "Wird geladen …" : LOAD_FAILED}
        </p>
      </>
    );
  }
  return (
    <>
      <PageHeading title="Berechtigungen" />
      <RoleForm onSaved={grants.reload} />
      {/* Keyed by the stored matrix, so a reload after a save starts from the server's answer. */}
      <GrantMatrix key={JSON.stringify(grants.data)} data={grants.data} reload={grants.reload} />
    </>
  );
}

function ElementLabel({
  element,
  label,
}: {
  readonly element: BackofficeElement;
  readonly label: string;
}): ReactElement {
  const tip = ELEMENT_TIPS[element];
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      {tip === undefined ? null : <HelpTip topic={label}>{tip}</HelpTip>}
    </span>
  );
}

function RoleForm({ onSaved }: { readonly onSaved: () => void }): ReactElement {
  return (
    <Section
      title={
        <span className="inline-flex items-center gap-1">
          Rolle hinzufügen
          <HelpTip topic="Rolle hinzufügen">{ROLE_HELP}</HelpTip>
        </span>
      }
    >
      <SaveForm
        label="Rolle hinzufügen"
        readOnly={false}
        submitLabel="Hinzufügen"
        onSaved={onSaved}
        submit={(form) => {
          const name = String(form.get("role_name") ?? "").trim();
          const label = String(form.get("role_label") ?? "").trim();
          return saveGrantRole(name, label);
        }}
      >
        <div className="grid gap-x-6 sm:grid-cols-2">
          <FormField
            label="Name der Rolle"
            name="role_name"
            required
            maxLength={64}
            pattern="[a-z][a-z0-9_\-]{0,63}"
            placeholder="vertrieb"
          />
          <FormField
            label="Bezeichnung"
            name="role_label"
            required
            maxLength={100}
            placeholder="Vertrieb"
          />
        </div>
      </SaveForm>
    </Section>
  );
}

function GrantMatrix({
  data,
  reload,
}: {
  readonly data: Grants;
  readonly reload: () => void;
}): ReactElement {
  const [matrix, setMatrix] = useState<Matrix>(() => matrixOf(data));
  const [removal, setRemoval] = useState<ActionOutcome | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (element: string, role: string, on: boolean): void => {
    setMatrix((current) => {
      const roles = new Set(current[element]);
      if (on) {
        roles.add(role);
      } else {
        roles.delete(role);
      }
      return { ...current, [element]: roles };
    });
  };

  const remove = (role: string): void => {
    setBusy(true);
    setRemoval(null);
    removeGrantRole(role)
      .then((outcome) => {
        setRemoval(outcome);
        if (outcome.ok) {
          reload();
        }
      })
      .catch((error: unknown) => {
        console.error("[PermissionsPage] remove failed", error);
        setRemoval({ ok: false, message: LOAD_FAILED });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Section
      title={
        <span className="inline-flex items-center gap-1">
          Wer darf was
          <HelpTip topic="Wer darf was">{NO_ROLE_HELP}</HelpTip>
        </span>
      }
    >
      {removal !== null && !removal.ok ? (
        <p className="mb-4 text-sm text-destructive" role="status">
          {removal.message}
        </p>
      ) : null}
      {/* The matrix lives in state: »Startwert« changes it without a change event. */}
      <SaveForm
        label="Berechtigungen"
        readOnly={busy}
        dirty={!sameMatrix(matrix, matrixOf(data))}
        onSaved={reload}
        submit={() =>
          saveGrants(
            Object.fromEntries(
              Object.entries(matrix).map(([element, roles]) => [element, [...roles]]),
            ),
          )
        }
      >
        {data.pages.map((page) => (
          <div className="mt-6 overflow-x-auto first:mt-0" key={page.page}>
            <Table>
              <TableCaption className="mb-2 caption-top text-left text-base font-semibold text-foreground">
                {page.page}
              </TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-48">Element</TableHead>
                  {data.columns.map((column) => (
                    <TableHead className="text-center align-bottom" key={column.role_name}>
                      <span className="block">{column.label}</span>
                      {column.built_in ? null : (
                        <Button
                          type="button"
                          size="sm"
                          variant="link"
                          className="h-auto p-0 text-xs"
                          disabled={busy}
                          aria-label={`${column.label} entfernen`}
                          onClick={() => {
                            remove(column.role_name);
                          }}
                        >
                          Entfernen
                        </Button>
                      )}
                    </TableHead>
                  ))}
                  <TableHead>
                    <span className="sr-only">Startwert</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.rows.map((row) => (
                  <TableRow key={row.element}>
                    <TableCell>
                      <ElementLabel element={row.element} label={row.label} />
                    </TableCell>
                    {data.columns.map((column) => (
                      <TableCell className="text-center" key={column.role_name}>
                        <input
                          type="checkbox"
                          className="border border-input"
                          name={`${row.element}:${column.role_name}`}
                          aria-label={`${row.label}: ${column.label}`}
                          checked={matrix[row.element]?.has(column.role_name) ?? false}
                          onChange={(event) => {
                            toggle(row.element, column.role_name, event.target.checked);
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell>
                      <Button
                        type="button"
                        size="sm"
                        variant="link"
                        className="h-auto p-0 text-xs"
                        aria-label={`${row.label}: Startwert`}
                        onClick={() => {
                          setMatrix((current) => ({ ...current, [row.element]: new Set(row.default) }));
                        }}
                      >
                        Startwert
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </SaveForm>
    </Section>
  );
}
